import { ApolloError } from "@apollo/client";
import { client } from "../graphql/client";
import { config } from "../config/config";
import { DELETE_PUSH_SUBSCRIPTION, UPSERT_PUSH_SUBSCRIPTION } from "../graphql/queries";

const PUSH_SW_URL = "/push-sw.js";

/** Výsledek synchronizace – `report` lze uživateli zkopírovat (i při `ok`). */
export type SyncPushResult = { ok: true; report: string } | { ok: false; report: string };

function nowIso(): string {
  return new Date().toISOString();
}

function truncateForReport(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, Math.max(0, max - 1)) + "…";
}

function apolloErrorToText(e: unknown): string {
  if (e instanceof ApolloError) {
    const gq = e.graphQLErrors.map((g) => g.message).filter(Boolean);
    if (gq.length) return gq.join("\n");
    const n = e.networkError;
    if (n && "message" in n) return (n as { message: string }).message;
    if (e.message) return e.message;
  }
  if (e instanceof Error) return e.message;
  return String(e);
}

function toUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function getOrCreateDeviceId(): string {
  let id = localStorage.getItem("device_id");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("device_id", id);
  }
  return id;
}

type SubResult =
  | { ok: true; subscription: PushSubscription }
  | { ok: false; message: string };

async function getOrCreatePushSubscription(
  registration: ServiceWorkerRegistration
): Promise<SubResult> {
  try {
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      const vapid = config.webPushPublicKey;
      if (!vapid) {
        return {
          ok: false,
          message: "V buildu chybí REACT_APP_WEB_PUSH_PUBLIC_KEY (VAPID public key).",
        };
      }
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: toUint8Array(vapid),
      });
    }
    return { ok: true, subscription };
  } catch (e) {
    const msg =
      e instanceof Error ? `${e.name}: ${e.message}` : String(e);
    return { ok: false, message: msg || "pushManager.subscribe() selhalo." };
  }
}

function baseReport(
  userId: string,
  extra: Record<string, string>
): string {
  const lines = [
    "=== Diagnostika push (upozornění) ===",
    `Čas: ${nowIso()}`,
    `userId: ${userId || "(prázdné)"}`,
    `deviceId: ${getOrCreateDeviceId()}`,
    `isSecureContext: ${String(typeof window !== "undefined" && window.isSecureContext)}`,
    `userAgent: ${truncateForReport(typeof navigator !== "undefined" ? navigator.userAgent : "", 400)}`,
    `REACT_APP_WEB_PUSH_PUBLIC_KEY: ${config.webPushPublicKey ? "nastaveno" : "CHYBÍ"}`,
    ...Object.entries(extra).map(([k, v]) => `${k}: ${v}`),
  ];
  return lines.join("\n");
}

export async function syncPushSubscription(): Promise<SyncPushResult> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
    const r = baseReport(localStorage.getItem("userId") || "", {
      "Chyba": "Prohlížeč nemá serviceWorker, PushManager nebo Notification API.",
    });
    console.warn("[push]", r);
    return { ok: false, report: r };
  }
  const userId = localStorage.getItem("userId") || "";
  if (!userId) {
    const r = baseReport("", {
      "Chyba": "Nejste přihlášen – userId není v localStorage, na backend se nic neodešle.",
    });
    console.info("[push]", r);
    return { ok: false, report: r };
  }

  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }

  if (permission !== "granted") {
    const r = baseReport(userId, {
      "Notification.permission": permission,
      "Chyba": "Oprávnění k oznámením není grantované (uživatel odmítl, nebo dialog není k dispozici).",
    });
    console.info("[push]", r);
    return { ok: false, report: r };
  }

  const extra: Record<string, string> = {
    "Notification.permission": permission,
  };

  try {
    await navigator.serviceWorker.register(PUSH_SW_URL);
    const registration = await navigator.serviceWorker.ready;
    const subres = await getOrCreatePushSubscription(registration);
    if (!subres.ok) {
      const r = baseReport(userId, {
        ...extra,
        "push subscription": "ne",
        "Důvod": subres.message,
      });
      console.warn("[push]", r);
      return { ok: false, report: r };
    }

    const json = subres.subscription.toJSON();
    const endpoint = json.endpoint;
    const p256dh = json.keys?.p256dh;
    const auth = json.keys?.auth;
    if (!endpoint || !p256dh || !auth) {
      const r = baseReport(userId, {
        ...extra,
        "endpoint (preview)": endpoint ? truncateForReport(endpoint, 80) : "(chybí)",
        "Chyba": "Z Push subscription chybí endpoint nebo klíče p256dh/auth.",
      });
      console.warn("[push]", r);
      return { ok: false, report: r };
    }

    const input = {
      userId,
      deviceId: getOrCreateDeviceId(),
      endpoint,
      p256dh,
      auth,
      permission,
      enabled: true,
      userAgent: navigator.userAgent,
    };

    const mut = await client.mutate<{
      upsertPushSubscription?: { success: boolean; message?: string | null };
    }>({
      mutation: UPSERT_PUSH_SUBSCRIPTION,
      variables: { input },
      fetchPolicy: "no-cache",
    });

    const payload = mut.data?.upsertPushSubscription;
    const apolloBlock = mut.errors?.length
      ? mut.errors.map((e) => e.message).join("; ")
      : null;

    if (apolloBlock) {
      const r = baseReport(userId, {
        ...extra,
        "endpoint (preview)": truncateForReport(endpoint, 96),
        "Odesláno na server": "pokus ano, GraphQL vrátil chyby v odpovědi",
        "GraphQL errors": apolloBlock,
      });
      console.error("[push]", r);
      return { ok: false, report: r };
    }

    if (payload && payload.success === false) {
      const r = baseReport(userId, {
        ...extra,
        "endpoint (preview)": truncateForReport(endpoint, 96),
        "Odpověď serveru (upsertPushSubscription)": `success=false, message=${payload.message ?? "(null)"}`,
      });
      console.warn("[push]", r);
      return { ok: false, report: r };
    }

    const successText =
      payload == null
        ? "Odpověď bez pole upsertPushSubscription (ověřte schéma / proxy)."
        : `success=${String(payload.success)}, message=${payload.message ?? ""}`;

    const r = baseReport(userId, {
      ...extra,
        "endpoint (preview)": truncateForReport(endpoint, 120),
      "Odesláno na server": "ano (mutation upsertPushSubscription)",
      "Odpověď": successText,
    });
    console.info("[push] OK", r);
    return { ok: true, report: r };
  } catch (err) {
    const text = apolloErrorToText(err);
    const r = baseReport(userId, {
      ...extra,
      "Chyba (catch)": text,
    });
    console.error("[push] Failed to sync push subscription", err);
    return { ok: false, report: r };
  }
}

export async function deleteCurrentPushSubscription(reason = "logout"): Promise<void> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration(PUSH_SW_URL);
    if (!registration) return;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;

    await client.mutate({
      mutation: DELETE_PUSH_SUBSCRIPTION,
      variables: {
        input: {
          endpoint: subscription.endpoint,
          reason,
        },
      },
      fetchPolicy: "no-cache",
    });
  } catch (err) {
    console.error("Failed to delete push subscription:", err);
  }
}
